/**
 * SOVRANOVA AssessmentFlow — PHQ-9 + GAD-7 questionnaire
 * Warm, non-clinical UI. Both instruments in sequence. Saves to backend on completion.
 */
import { useState } from 'react'
import { api } from '../../services/api'
import { resolveRegion, getCrisisResources, HELPLINE_DIRECTORY_URL } from '../../services/crisisResources'

const PHQ9_DEF = {
  id: 'PHQ-9',
  intro: 'Over the last 2 weeks, how often have you been bothered by any of the following?',
  questions: [
    'Little interest or pleasure in doing things',
    'Feeling down, depressed, or hopeless',
    'Trouble falling or staying asleep, or sleeping too much',
    'Feeling tired or having little energy',
    'Poor appetite or overeating',
    'Feeling bad about yourself — or that you are a failure, or have let yourself or your family down',
    'Trouble concentrating on things, such as reading or watching television',
    'Moving or speaking so slowly that others could have noticed — or being so fidgety or restless that you have been moving around a lot more than usual',
    'Thoughts that you would be better off dead, or of hurting yourself in some way',
  ],
  maxScore: 27,
  severity: (s) => s <= 4 ? { label: 'Minimal', color: '#7a9e9f' }
    : s <= 9  ? { label: 'Mild',     color: '#e6a020' }
    : s <= 14 ? { label: 'Moderate', color: '#d4711a' }
    : s <= 19 ? { label: 'Moderately Severe', color: '#c0392b' }
    :           { label: 'Severe',   color: '#922b21' },
  message: (sev) => ({
    minimal: 'You are doing well. Staying connected to your wellbeing is exactly what this is for.',
    mild: 'Some difficulty is present. SOVRANOVA is here to support you through it.',
    moderate: 'This level of difficulty is meaningful. We recommend speaking with a professional alongside SOVRANOVA.',
    moderately_severe: 'What you are experiencing deserves proper care. Please speak with a doctor (GP) or psychologist — SOVRANOVA supports alongside that, not instead of it.',
    severe: 'What you are experiencing is significant. Please reach out to a mental health professional. You do not have to carry this alone.',
  })[sev] || '',
}

const GAD7_DEF = {
  id: 'GAD-7',
  intro: 'Still thinking about the last 2 weeks — how often have you been bothered by:',
  questions: [
    'Feeling nervous, anxious, or on edge',
    'Not being able to stop or control worrying',
    'Worrying too much about different things',
    'Trouble relaxing',
    'Being so restless that it is hard to sit still',
    'Becoming easily annoyed or irritable',
    'Feeling afraid as if something awful might happen',
  ],
  maxScore: 21,
  severity: (s) => s <= 4 ? { label: 'Minimal', color: '#7a9e9f' }
    : s <= 9  ? { label: 'Mild',     color: '#e6a020' }
    : s <= 14 ? { label: 'Moderate', color: '#d4711a' }
    :           { label: 'Severe',   color: '#c0392b' },
  message: (sev) => ({
    minimal: 'Anxiety is well-managed right now. Keep checking in.',
    mild: "Some anxiety is present. SOVRANOVA's breathing and regulation tools can help.",
    moderate: 'Anxiety is affecting your daily life. Professional support is recommended alongside SOVRANOVA.',
    severe: 'Significant anxiety is present. Please speak with a mental health professional — this level deserves proper care.',
  })[sev] || '',
}

const RESPONSE_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' },
]

// Region-resolved from the registry (international beta wave, Sep 2026). For an
// unknown region this names no country's number — the directory and the local
// emergency number only. Red-team H8 closed.
function crisisLines(region) {
  const res = getCrisisResources(region)
  const primary = res.primary.tel ? `${res.primary.label} ${res.primary.number}` : `${res.primary.label}: ${HELPLINE_DIRECTORY_URL}`
  const emergency = res.emergency.tel ? `${res.emergency.label} ${res.emergency.number}` : 'your local emergency number'
  return { primary, emergency }
}
function crisisMsg(region) {
  const { primary, emergency } = crisisLines(region)
  return `If you are having thoughts of harming yourself, please reach out now — ${primary} · ${emergency}`
}

function calcScore(responses) { return responses.reduce((sum, r) => sum + r, 0) }
function normSeverity(label)   { return label.toLowerCase().replace(/\s+/g, '_') }

export function AssessmentFlow({ onComplete, sessionId = null }) {
  const [phase, setPhase]       = useState('phq9')   // phq9 | gad7 | results
  const [qIndex, setQIndex]     = useState(0)
  const [phq9R, setPHQ9]        = useState([])
  const [gad7R, setGAD7]        = useState([])
  const [answer, setAnswer]     = useState(null)
  const [crisis, setCrisis]     = useState(false)
  const [results, setResults]   = useState(null)
  const [saving, setSaving]     = useState(false)
  const [saveErr, setSaveErr]   = useState(null)

  const inst    = phase === 'phq9' ? PHQ9_DEF : GAD7_DEF
  const qs      = inst.questions
  const isLastQ = qIndex === qs.length - 1
  const total   = PHQ9_DEF.questions.length + GAD7_DEF.questions.length
  const progress = (phase === 'phq9' ? qIndex : PHQ9_DEF.questions.length + qIndex) + 1

  const handleNext = async () => {
    if (answer === null) return
    const prev = phase === 'phq9' ? phq9R : gad7R
    const next = [...prev, answer]
    if (phase === 'phq9') setPHQ9(next); else setGAD7(next)
    if (phase === 'phq9' && qIndex === 8 && answer > 0) setCrisis(true)
    setAnswer(null)
    if (!isLastQ) { setQIndex(q => q + 1) }
    else if (phase === 'phq9') { setPhase('gad7'); setQIndex(0) }
    else { await finish(phq9R, next) }
  }

  const finish = async (phq9, gad7) => {
    setSaving(true); setSaveErr(null)
    const p9score = calcScore(phq9), g7score = calcScore(gad7)
    const p9sev   = normSeverity(PHQ9_DEF.severity(p9score).label)
    const g7sev   = normSeverity(GAD7_DEF.severity(g7score).label)
    try {
      await Promise.all([
        // D0-G2: send the region so the item-9 gate's resources — the ones
        // returned AND the ones recorded in crisis_events.resources_shown — are
        // this person's, not the GLOBAL "find a helpline in your country" set.
        api.post('/outcomes/phq9', { score: p9score, severity: p9sev, responses: phq9, sessionId, region: resolveRegion() }),
        api.post('/outcomes/gad7', { score: g7score, severity: g7sev, responses: gad7, sessionId }),
      ])
    } catch (_) { setSaveErr('Could not save — check your connection.') }
    setResults({
      phq9: { score: p9score, ...PHQ9_DEF.severity(p9score), message: PHQ9_DEF.message(p9sev) },
      gad7: { score: g7score, ...GAD7_DEF.severity(g7score), message: GAD7_DEF.message(g7sev) },
    })
    setPhase('results'); setSaving(false)
  }

  if (phase === 'results' && results) {
    return (
      <div className="assessment-screen">
        <div className="assessment-card">
          <div className="assessment-mark">◎</div>
          <h2 className="assessment-title">Your Wellbeing Check-in</h2>
          <p className="assessment-subtitle">This is information, not judgement. It helps SOVRANOVA support you better.</p>
          <div className="result-bands">
            {[{ label: 'Mood & Depression', id: 'PHQ-9', max: 27, data: results.phq9 },
              { label: 'Anxiety',           id: 'GAD-7', max: 21, data: results.gad7 }].map(({ label, id, max, data }) => (
              <div key={id} className="result-band">
                <div className="result-band-header">
                  <span className="result-band-label">{label} ({id})</span>
                  <span className="result-band-score" style={{ color: data.color }}>{data.score}/{max} · {data.label}</span>
                </div>
                <div className="result-bar-track">
                  <div className="result-bar-fill" style={{ width: `${(data.score / max) * 100}%`, background: data.color }} />
                </div>
                <p className="result-message">{data.message}</p>
              </div>
            ))}
          </div>
          {saveErr && <p style={{ color: 'var(--crisis-red)', fontSize: '0.85rem', textAlign: 'center' }}>{saveErr}</p>}
          <p className="assessment-notice">Your results are private and encrypted. Used only to personalise your experience and track progress.</p>
          <button className="assessment-btn" onClick={onComplete}>Continue to SOVRANOVA</button>
        </div>
      </div>
    )
  }

  return (
    <div className="assessment-screen">
      <div className="assessment-card">
        <div className="assessment-progress-header">
          <span className="assessment-instrument-label">{inst.id}</span>
          <span className="assessment-counter">{progress} / {total}</span>
        </div>
        <div className="assessment-progress-bar">
          <div className="assessment-progress-fill" style={{ width: `${(progress / total) * 100}%` }} />
        </div>
        {qIndex === 0 && <p className="assessment-intro">{inst.intro}</p>}
        <h3 className="assessment-question">{qs[qIndex]}</h3>
        {phase === 'phq9' && qIndex === 8 && (
          <p className="assessment-crisis-note">If you are having these thoughts right now — {crisisLines(resolveRegion()).primary}</p>
        )}
        {crisis && phase === 'gad7' && qIndex === 0 && (
          <div className="assessment-crisis-banner">{crisisMsg(resolveRegion())}</div>
        )}
        <div className="assessment-options">
          {RESPONSE_OPTIONS.map(opt => (
            <button key={opt.value}
              className={`assessment-option ${answer === opt.value ? 'selected' : ''}`}
              onClick={() => setAnswer(opt.value)}>
              <span className="option-dot" />{opt.label}
            </button>
          ))}
        </div>
        <button className="assessment-btn" onClick={handleNext} disabled={answer === null || saving}>
          {saving ? 'Saving…' : isLastQ && phase === 'gad7' ? 'Complete Check-in' : 'Next'}
        </button>
      </div>
    </div>
  )
}
